import { useTranslation } from "react-i18next";
import TopBar from "@/pages/home/components/TopBar";
import Header from "@/pages/home/components/Header";
import Footer from "@/pages/home/components/Footer";
import StickyMobile from "@/pages/home/components/StickyMobile";
import Breadcrumb from "./components/Breadcrumb";
import Hero from "./components/Hero";
import WanneerSlotenmaker from "./components/WanneerSlotenmaker";
import Diensten from "./components/Diensten";
import SpoedService from "./components/SpoedService";
import Prijzen from "./components/Prijzen";
import WaaromWij from "./components/WaaromWij";
import FAQ from "./components/FAQ";
import InterneLinks from "./components/InterneLinks";
import CTABanner from "./components/CTABanner";

function SchemaMarkup() {
  const { t } = useTranslation("slotenmaker-eindhoven");
  const faqItems = t("faq.items", { returnObjects: true }) as Array<{
    q: string;
    a: string;
  }>;

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: "Slotenmaker Eindhoven",
    url: "https://ramen-repair-eindhoven.nl/slotenmaker-eindhoven/",
    telephone: "+31407987470",
    email: "info@ramen-repair-eindhoven.nl",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Marconilaan 3",
      postalCode: "5612 HM",
      addressLocality: "Eindhoven",
      addressRegion: "Noord-Brabant",
      addressCountry: "NL",
    },
    areaServed: {
      "@type": "City",
      name: "Eindhoven",
    },
    serviceType: "Slotenmaker diensten",
    description:
      "Slotenmaker nodig in Eindhoven ? Buitengesloten, slot kapot of inbraakschade ? Wij komen 24/7 met spoedservice. Vaste prijzen, met SKG-gekeurde producten.",
    openingHours: ["Mo-Su 00:00-23:59"],
    priceRange: "€",
  };

  const emergencySchema = {
    "@context": "https://schema.org",
    "@type": "EmergencyService",
    name: "Ramen Repair Eindhoven — Slotenmaker spoedservice 24/7",
    areaServed: {
      "@type": "City",
      name: "Eindhoven",
    },
    availableChannel: {
      "@type": "ServiceChannel",
      servicePhone: {
        "@type": "ContactPoint",
        telephone: "+31407987470",
        contactType: "emergency",
        availableLanguage: "Dutch",
      },
    },
    openingHours: ["Mo-Su 00:00-23:59"],
    isAvailable: true,
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://ramen-repair-eindhoven.nl/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Slotenmaker Eindhoven",
        item: "https://ramen-repair-eindhoven.nl/slotenmaker-eindhoven/",
      },
    ],
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: Array.isArray(faqItems)
      ? faqItems.map((item) => ({
          "@type": "Question",
          name: item.q,
          acceptedAnswer: {
            "@type": "Answer",
            text: item.a,
          },
        }))
      : [],
  };

  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify(localBusinessSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(emergencySchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
    </>
  );
}

export default function SlotenmakerEindhoven() {
  const { t } = useTranslation("slotenmaker-eindhoven");

  return (
    <main className="bg-white min-h-screen">
      <title>{t("seo.title")}</title>
      <meta name="description" content={t("seo.description")} />
      <meta property="og:title" content={t("seo.title")} />
      <meta property="og:description" content={t("seo.description")} />
      <meta property="og:locale" content="nl_NL" />
      <meta property="og:type" content="website" />
      <meta
        property="og:url"
        content="https://ramen-repair-eindhoven.nl/slotenmaker-eindhoven/"
      />
      <meta
        property="og:image"
        content="https://readdy.ai/api/search-image?query=Professional%20locksmith%20hands%20working%20on%20a%20modern%20front%20door%20lock%20mechanism%20close-up%20dark%20moody%20atmosphere%20warm%20lighting%20editorial%20photography%20yellow%20tool%20accents%20no%20faces%20visible&width=1200&height=630&seq=ogslotenmaker1&orientation=landscape"
      />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta
        property="og:image:alt"
        content="Slotenmaker Eindhoven — 24/7 spoedservice"
      />
      <link
        rel="canonical"
        href="https://ramen-repair-eindhoven.nl/slotenmaker-eindhoven/"
      />
      <link
        rel="alternate"
        hreflang="nl-NL"
        href="https://ramen-repair-eindhoven.nl/slotenmaker-eindhoven/"
      />
      <link
        rel="alternate"
        hreflang="x-default"
        href="https://ramen-repair-eindhoven.nl/slotenmaker-eindhoven/"
      />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={t("seo.title")} />
      <meta name="twitter:description" content={t("seo.description")} />
      <meta
        name="twitter:image"
        content="https://readdy.ai/api/search-image?query=Professional%20locksmith%20hands%20working%20on%20a%20modern%20front%20door%20lock%20mechanism%20close-up%20dark%20moody%20atmosphere%20warm%20lighting%20editorial%20photography%20yellow%20tool%20accents%20no%20faces%20visible&width=1200&height=630&seq=ogslotenmaker1&orientation=landscape"
      />
      <SchemaMarkup />
      <TopBar />
      <Header />
      <Breadcrumb />
      <Hero />
      <WanneerSlotenmaker />
      <Diensten />
      <SpoedService />
      <Prijzen />
      <WaaromWij />
      <FAQ />
      <InterneLinks />
      <CTABanner />
      <Footer />
      <StickyMobile />
    </main>
  );
}